// client/src/pages/GroupMembers.js
import React, { useEffect, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiFetch, fetchMe } from '../lib/api';
import { DateTime } from 'luxon';
import logo from '../assets/chitconnect-logo-blue.png';

/* ---------- helpers ---------- */
// robust date parser that safely handles ISO, "YYYY-MM-DD", and Date objects
function parseDateLike(v) {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v) ? null : v;
  if (typeof v === 'number') return new Date(v);
  if (typeof v === 'string') {
    const ymd = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (ymd) {
      // avoid TZ shift by anchoring to midnight UTC
      return new Date(`${ymd[1]}-${ymd[2]}-${ymd[3]}T00:00:00Z`);
    }
    const d = new Date(v);
    if (!isNaN(d)) return d;
  }
  return null;
}

function parseUtcDateTime(v) {
  if (!v) return null;
  const s = String(v);
  // We store/compare schedule times in UTC; schedule rows may come back as ISO (with T) or SQL DATETIME.
  const dt = s.includes('T')
    ? DateTime.fromISO(s, { zone: 'utc' })
    : DateTime.fromSQL(s, { zone: 'utc' });
  return dt.isValid ? dt : null;
}

// DD/MM/YYYY (en-GB)
function fmtDDMMYYYY(v) {
  const d = parseDateLike(v);
  return d ? d.toLocaleDateString('en-GB') : '—';
}
// small hook to switch to cards on narrow viewports
function useIsMobile(breakpoint = 768) {
  const get = () => (typeof window !== 'undefined' ? window.innerWidth < breakpoint : true);
  const [isMobile, set] = useState(get);
  useEffect(() => {
    const onResize = () => set(get());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

// Different member endpoints can shape ids differently:
// - /api/groups/:id/members: `id` may be the group_members row id; `user_id` is the user id.
// - /api/group-members/:groupId: `id` is aliased to the user id, but `user_id` is still present.
// Always use this helper when you need to group/compare by *user*.
const getUserIdFromRow = (r) => Number(r?.user_id ?? r?.id);
/* -------------------------------- */

export default function GroupMembers() {
  const { groupId } = useParams();

  const myUserId = (() => {
    try {
      const raw = sessionStorage.getItem('user');
      if (!raw) return null;
      const u = JSON.parse(raw);
      return Number(u?.id ?? u?.user?.id ?? u?.user_id ?? u?.Use_ID ?? u?.use_id ?? u?.User_ID ?? u?.UserId ?? u?.userId ?? null);
    } catch {
      return null;
    }
  })();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [group, setGroup] = useState(null);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pendingMsg, setPendingMsg] = useState('');

  const [biddingStarted, setBiddingStarted] = useState(null); // null = unknown
  const [firstBidStartUtc, setFirstBidStartUtc] = useState(null);
  const [removeBusyId, setRemoveBusyId] = useState(null);
  const [removeError, setRemoveError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPending, setSelectedPending] = useState(null);
  const [approveCount, setApproveCount] = useState(1);

  const [premiumRows, setPremiumRows] = useState([]);
  const [premiumMap, setPremiumMap] = useState({});
  const [premiumLoading, setPremiumLoading] = useState(false);
  const [premiumSaving, setPremiumSaving] = useState(false);
  const [premiumMsg, setPremiumMsg] = useState('');
  const [premiumErr, setPremiumErr] = useState('');


  const isMobile = useIsMobile();
  const isSuperadmin = useMemo(() => String(me?.access_level || me?.role || "").toLowerCase() === "superadmin", [me]);


  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const res = await apiFetch(`/api/group-members/${groupId}`);
        const json = await res.json();
        if (!alive) return;

        if (Array.isArray(json)) setItems(json);
        else if (json && Array.isArray(json.rows)) setItems(json.rows);
        else setItems([]);

        // Load current user (best effort) for organiser approval UI.
        // IMPORTANT: use fetchMe() because /api/users/me returns { ok, user } and /api/me returns { ok, user }.
        try {
          const u = await fetchMe();
          if (alive) setMe(u);
        } catch {}

        try {
          const gRes = await apiFetch(`/api/groups/${groupId}`);
          const gJson = await gRes.json().catch(() => null);
          if (alive && gRes.ok) setGroup(gJson);
        } catch {}
      } catch (e) {
        setErr(e?.message || 'Failed to fetch group members');
        setItems([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [groupId]);

  // Superadmin: load membership rows for premium management (per-membership).
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!isSuperadmin) {
        setPremiumRows([]);
        setPremiumMap({});
        return;
      }

      setPremiumLoading(true);
      setPremiumErr("");
      setPremiumMsg("");

      try {
        const res = await apiFetch("/api/groups/" + groupId + "/members");
        const json = await res.json().catch(() => ({}));
        if (!alive) return;

        if (res.ok && json && Array.isArray(json.members)) {
          const list = json.members.filter((r) => Number(r?.is_removed || 0) === 0);
          setPremiumRows(list);
          const map = {};
          for (const r of list) map[r.id] = Number(r?.is_premium) === 1;
          setPremiumMap(map);
        } else {
          setPremiumRows([]);
          setPremiumMap({});
          setPremiumErr(json?.error || "Failed to load premium memberships");
        }
      } catch (e) {
        if (alive) setPremiumErr(e?.message || "Failed to load premium memberships");
      } finally {
        if (alive) setPremiumLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [groupId, isSuperadmin]);

  const setAllPremium = (value) => {
    const map = {};
    for (const r of premiumRows) {
      map[r.id] = !!value;
    }
    setPremiumMap(map);
  };

  const savePremiumFlags = async () => {
    if (!isSuperadmin) return;
    setPremiumSaving(true);
    setPremiumErr("");
    setPremiumMsg("");

    try {
      const items = premiumRows.map((r) => ({ id: r.id, is_premium: premiumMap[r.id] ? 1 : 0 }));

      const res = await apiFetch("/api/groups/" + groupId + "/members/premium", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPremiumErr(json?.error || "Failed to save premium flags");
      } else {
        setPremiumMsg("✅ Premium flags saved");

        // Refresh from server
        try {
          const res2 = await apiFetch("/api/groups/" + groupId + "/members");
          const json2 = await res2.json().catch(() => ({}));
          if (res2.ok && json2 && Array.isArray(json2.members)) {
            const list = json2.members.filter((r) => Number(r?.is_removed || 0) === 0);
            setPremiumRows(list);
            const map = {};
            for (const r of list) map[r.id] = Number(r?.is_premium) === 1;
            setPremiumMap(map);
          }
        } catch {}
      }
    } catch (e) {
      setPremiumErr(e?.message || "Failed to save premium flags");
    } finally {
      setPremiumSaving(false);
    }
  };


  /* biddingStarted effect */
  useEffect(() => {
    let alive = true;
    (async () => {
      setBiddingStarted(null);
      setFirstBidStartUtc(null);
      if (!groupId) return;

      const fallbackFromGroup = () => {
        const d = String(group?.auction_start_date || '').trim();
        const t = String(group?.start_time || '').trim();
        const z = String(group?.timezone || group?.time_zone || '').trim() || 'utc';
        if (!d || !t) return null;
        const iso = `${d}T${t.length >= 8 ? t.slice(0, 8) : t}`;
        const dtLocal = DateTime.fromISO(iso, { zone: z });
        if (!dtLocal.isValid) return null;
        return dtLocal.toUTC();
      };

      try {
        const res = await apiFetch(`/api/groups/${groupId}/schedule`);
        const data = await res.json().catch(() => []);

        let firstStart = null;
        if (res.ok && Array.isArray(data) && data.length) {
          // Prefer period 1; otherwise use the lowest period_number
          let row = data.find((r) => Number(r?.period_number) === 1) || null;
          if (!row) {
            row = data.reduce((best, cur) => {
              const bp = best ? Number(best?.period_number) : Infinity;
              const cp = Number(cur?.period_number);
              return cp < bp ? cur : best;
            }, null);
          }
          firstStart = parseUtcDateTime(row?.start_time);
        }

        if (!firstStart) firstStart = fallbackFromGroup();

        if (alive) {
          if (firstStart && firstStart.isValid) {
            setFirstBidStartUtc(firstStart.toISO());
            setBiddingStarted(DateTime.utc() >= firstStart);
          } else {
            setBiddingStarted(null);
          }
        }
      } catch {
        const fb = fallbackFromGroup();
        if (alive && fb && fb.isValid) {
          setFirstBidStartUtc(fb.toISO());
          setBiddingStarted(DateTime.utc() >= fb);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [groupId, group?.auction_start_date, group?.start_time, group?.timezone, group?.time_zone]);

  const rows = useMemo(() => (Array.isArray(items) ? items : []), [items]);

  // Approved members shown in the main list/table.
  // Pending (is_approved_by_organiser=false/0) are handled in the organiser approval panel.
  const approvedRows = useMemo(() => {
    return rows.filter((r) => {
      const v = r?.is_approved_by_organiser;
      // If the backend doesn't send the flag, treat as approved to avoid hiding everyone.
      if (v === undefined || v === null) return true;
      return Number(v) !== 0;
    });
  }, [rows]);

  // Collapse multiple membership rows per user into a single display row.
  // Each approved membership is represented by a row in `group_members`.
  const approvedMembers = useMemo(() => {
    const m = new Map();
    for (const r of approvedRows) {
      const isRemoved = Number(r?.is_removed) === 1;
      if (isRemoved) continue;

      const userId = getUserIdFromRow(r);
      if (!userId) continue;

      const existing = m.get(userId) || {
        id: userId,
        given_name: r.given_name,
        surname: r.surname,
        email: r.email,
        mobile: r.mobile,
        role: r.role,
        joined_at: r.joined_at || null,

        // internal aggregations (not rendered)
        _idx: new Set(),
        _rowCount: 0,
        _approvedCountHint: 0,
      };

      // If membership_index is present, we're in "row-per-membership" mode.
      // IMPORTANT: membership_index is an identifier, NOT a count (it can be 2 even if the user has 1 active membership).
      const idx = r?.membership_index;
      if (idx !== undefined && idx !== null && Number.isFinite(Number(idx))) {
        existing._idx.add(Number(idx));
      } else {
        // Legacy/compact mode: a single row per user (count comes from approved_count).
        existing._rowCount += 1;
      }

      const approvedCount = Number(r?.approved_count);
      if (Number.isFinite(approvedCount) && approvedCount > existing._approvedCountHint) {
        existing._approvedCountHint = approvedCount;
      }

      // Prefer organiser role if any row says organiser
      const role = String(r?.role || '').toLowerCase();
      if (role === 'organiser') existing.role = 'organiser';

      // Keep earliest joined_at (if present)
      if (r?.joined_at) {
        const a = parseDateLike(existing.joined_at);
        const b = parseDateLike(r.joined_at);
        if (!a || (b && b < a)) existing.joined_at = r.joined_at;
      }

      m.set(userId, existing);
    }

    const out = Array.from(m.values()).map((x) => {
      const hasIndex = x._idx && x._idx.size > 0;
      let membershipCount = 0;
      if (hasIndex) {
        membershipCount = x._idx.size;
      } else if (x._approvedCountHint > 0) {
        membershipCount = x._approvedCountHint;
      } else {
        membershipCount = x._rowCount;
      }

      // Strip internals before rendering
      // eslint-disable-next-line no-unused-vars
      const { _idx, _rowCount, _approvedCountHint, ...clean } = x;
      return { ...clean, membershipCount };
    });

    return out.sort((a, b) => {
      const an = `${a.given_name || ''} ${a.surname || ''}`.trim().toLowerCase();
      const bn = `${b.given_name || ''} ${b.surname || ''}`.trim().toLowerCase();
      return an.localeCompare(bn);
    });
  }, [approvedRows]);

  const pending = useMemo(() => {
    const meId = me?.id != null ? Number(me.id) : null;
    const m = new Map();
    for (const r of rows) {
      const isPending = Number(r?.is_approved_by_organiser) === 0;
      const isRemoved = Number(r?.is_removed) === 1;
      const role = String(r?.role || '').toLowerCase();
      if (!isPending || isRemoved) continue;
      if (role && role !== 'member') continue;
      const key = getUserIdFromRow(r);
      // Never show "pending" approvals for yourself (eg. if organiser accidentally accepted an invite earlier)
      if (meId && key === meId) continue;
      const curr = m.get(key) || {
        user_id: key,
        given_name: r.given_name,
        surname: r.surname,
        email: r.email,
        count: 0,
      };
      const hintedReq = Number(r?.requested_count);
      if (Number.isFinite(hintedReq) && hintedReq > 0) {
        curr.count = Math.max(curr.count, hintedReq);
      } else {
        const hintedApproved = Number(r?.approved_count);
        if (Number.isFinite(hintedApproved) && hintedApproved > 0) {
          curr.count = Math.max(curr.count, hintedApproved);
        } else {
          curr.count += 1;
        }
      }
      m.set(key, curr);
    }
    return Array.from(m.values()).sort((a, b) => {
      const an = `${a.given_name || ''} ${a.surname || ''}`.trim().toLowerCase();
      const bn = `${b.given_name || ''} ${b.surname || ''}`.trim().toLowerCase();
      return an.localeCompare(bn);
    });
  }, [rows, me]);

  const canRemoveMembers = useMemo(() => {
    if (!me) return false;
    const meId = me?.id != null ? Number(me.id) : null;
    if (!meId) return false;

    const isOwner = meId != null && group && Number(group.owner_id) === meId;
    const isOrganiserHere =
      meId != null &&
      rows.some((r) => {
        const uid = getUserIdFromRow(r);
        const role = String(r?.role || '').toLowerCase();
        const isOrgFlag = Number(r?.is_organiser) === 1;
        return uid === meId && (role === 'organiser' || isOrgFlag);
      });

    return (isOwner || isOrganiserHere) && biddingStarted === false;
  }, [me, group, rows, biddingStarted]);

  const canRemoveMemberRow = (member) => {
    if (!canRemoveMembers) return false;
    const memberId = Number(member?.id);
    const meId = Number(me?.id);
    const ownerId = Number(group?.owner_id);
    const isSelf = memberId && meId && memberId === meId;
    const isOrganiser = Number(member?.is_organiser) === 1 || (ownerId && memberId === ownerId) || String(member?.role || '').toLowerCase() === 'organiser';
    return !isSelf && !isOrganiser;
  };


  const canManage = useMemo(() => {
    if (!me) return false;

    const level = String(me.access_level || me.role || '').toLowerCase();
  const isSuperAdmin = level === 'superadmin';
  // Privacy: never expose emails/mobiles in group member lists
  const showContactDetails = false; // set to isSuperAdmin if you ever want superadmin-only details
    const isSuperadmin = level === 'superadmin';

    const meId = me?.id != null ? Number(me.id) : null;
    const isOwner = meId != null && group && Number(group.owner_id) === meId;

    // Fallback: if group fetch is blocked, infer organiser from the members list
    const isOrganiserHere =
      meId != null &&
      rows.some((r) => {
        const uid = getUserIdFromRow(r);
        const role = String(r?.role || '').toLowerCase();
        const isOrgFlag = Number(r?.is_organiser) === 1;
        return uid === meId && (role === 'organiser' || isOrgFlag);
      });

    return isSuperadmin || isOwner || isOrganiserHere;
  }, [me, group, rows]);


  const refreshMembers = async () => {
    try {
      const res = await apiFetch(`/api/group-members/${groupId}`);
      const json = await res.json().catch(() => ({}));
      if (Array.isArray(json)) setItems(json);
      else if (json && Array.isArray(json.rows)) setItems(json.rows);
      else setItems([]);
    } catch {}
  };

  const removeMember = async (userId, label) => {
    if (!canRemoveMembers) return;
    const uid = Number(userId);
    if (!Number.isFinite(uid) || uid <= 0) {
      setRemoveError(`Invalid userId (${String(userId)})`);
      return;
    }
    const name = label || 'this member';
    const ok = window.confirm(`Remove ${name} from this group? This will remove all of their memberships.`);
    if (!ok) return;

    setRemoveBusyId(uid);
    setRemoveError('');
    try {
      const res = await apiFetch(`/api/groups/${groupId}/members/${uid}/remove`, {
        method: 'POST',
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      await refreshMembers();
    } catch (e) {
      setRemoveError(e?.message || 'Failed to remove member');
    } finally {
      setRemoveBusyId(null);
    }
  };

  const openApprove = (p) => {
    setSelectedPending(p);
    setApproveCount(p?.count || 1);
    setModalOpen(true);
    setPendingMsg('');
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedPending(null);
  };

  const approveSelected = async () => {
    if (!selectedPending) return;
    const n = Math.max(1, Math.min(Number(approveCount) || 1, selectedPending.count || 1));
    setBusy(true);
    setPendingMsg('');
    try {
      const res = await apiFetch(`/api/groups/${groupId}/approve-member`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: selectedPending.user_id, approved_count: n }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPendingMsg(data?.error || `Approval failed (HTTP ${res.status})`);
        return;
      }
      setPendingMsg(`✅ Approved ${n} membership(s) for ${selectedPending.given_name || ''} ${selectedPending.surname || ''}`.trim());
      closeModal();
      await refreshMembers();
    } catch {
      setPendingMsg('Approval failed (server error)');
    } finally {
      setBusy(false);
    }
  };

  const rejectPending = async (userId) => {
    setBusy(true);
    setPendingMsg('');
    try {
      const res = await apiFetch(`/api/groups/${groupId}/reject-member/${userId}`, {
        method: 'DELETE',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPendingMsg(data?.error || `Rejection failed (HTTP ${res.status})`);
        return;
      }
      setPendingMsg('✅ Rejected pending request');
      await refreshMembers();
    } catch {
      setPendingMsg('Rejection failed (server error)');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style={{ background: '#f6f7f9', minHeight: '100vh' }}>
      {/* Top bar with logo */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '16px 20px' }}>
        <img src={logo} alt="ChitConnect" style={{ height: 28 }} />
      </div>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px 40px' }}>
        <div
          style={{
            background: '#fff',
            borderRadius: 10,
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              background: '#eef5ff',
              borderBottom: '1px solid #e7edf6',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#0b3b6b' }}>
              Members in Group #{groupId}
            </h1><div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
  <Link
    to={`/messages/group/${groupId}`}
    style={{
      background: '#16a34a',
      color: '#fff',
      padding: '8px 12px',
      borderRadius: 6,
      textDecoration: 'none',
      fontWeight: 700,
    }}
  >
    💬 Group Chat
  </Link>
  <Link
    to="/groups"
    style={{
      background: '#0b3b6b',
      color: '#fff',
      padding: '8px 12px',
      borderRadius: 6,
      textDecoration: 'none',
      fontWeight: 600,
    }}
  >
    ← Back to Groups
  </Link>
</div>
          </div>

          {/* Body */}
          <div style={{ padding: 20 }}>
            {loading && (
              <div style={noteBox}>Loading…</div>
            )}

            {err && !loading && (
              <div style={{ ...noteBox, border: '1px solid #fde2e2', background: '#fff5f5', color: '#b91c1c' }}>
                {err}
              </div>
            )}

            {isSuperadmin && !loading && (
              <div style={{ ...noteBox, border: '1px solid #e5e7eb', background: '#f9fafb', marginBottom: 16, color: '#111827' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 800, marginBottom: 4 }}>Premium memberships (Superadmin)</div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>
                      Premium is set per membership row. A user can have multiple memberships with different premium status.
                    </div>
                  </div>
                </div>

                {premiumErr && (
                  <div style={{ marginTop: 10, fontSize: 14, color: '#b91c1c' }}>{premiumErr}</div>
                )}
                {premiumMsg && (
                  <div style={{ marginTop: 10, fontSize: 14, color: '#047857' }}>{premiumMsg}</div>
                )}

                {premiumLoading ? (
                  <div style={{ marginTop: 10, fontSize: 14, color: '#6b7280' }}>Loading memberships…</div>
                ) : (
                  <div style={{ marginTop: 12, overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f8fafc' }}>
                          <th style={{ ...thBase, textAlign: 'left' }}>Member</th>
                          <th style={{ ...thBase, textAlign: 'center' }}>Membership #</th>
                          <th style={{ ...thBase, textAlign: 'center' }}>Premium</th>
                        </tr>
                      </thead>
                      <tbody>
                        {premiumRows.map((r) => {
                          const label = ((r.given_name || '') + ' ' + (r.surname || '')).trim() || r.email || ('User ' + r.user_id);
                          const checked = !!premiumMap[r.id];
                          return (
                            <tr key={r.id}>
                              <td style={tdLeft}>{label}</td>
                              <td style={tdCenter}>{r.membership_index ?? 0}</td>
                              <td style={tdCenter}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => setPremiumMap((prev) => ({ ...prev, [r.id]: e.target.checked }))}
                                  disabled={premiumSaving}
                                />
                              </td>
                            </tr>
                          );
                        })}
                        {premiumRows.length === 0 && (
                          <tr>
                            <td style={tdLeft} colSpan={3}>No active membership rows found.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <div style={{ marginTop: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="action-button"
                    style={{ padding: '8px 10px', fontSize: 13, opacity: premiumSaving ? 0.6 : 1 }}
                    disabled={premiumSaving || premiumLoading || premiumRows.length === 0}
                    onClick={() => setAllPremium(true)}
                  >
                    Select all premium
                  </button>

                  <button
                    type="button"
                    className="action-button"
                    style={{ padding: '8px 10px', fontSize: 13, opacity: premiumSaving ? 0.6 : 1 }}
                    disabled={premiumSaving || premiumLoading || premiumRows.length === 0}
                    onClick={() => setAllPremium(false)}
                  >
                    Clear all
                  </button>

                  <button
                    type="button"
                    className="action-button"
                    style={{ padding: '8px 10px', fontSize: 13, background: '#111827', color: '#fff', opacity: premiumSaving ? 0.6 : 1 }}
                    disabled={premiumSaving || premiumLoading || premiumRows.length === 0}
                    onClick={savePremiumFlags}
                  >
                    {premiumSaving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            )}

            {!loading && !err && canManage && pending.length > 0 && (
              <div style={{ ...noteBox, border: '1px solid #e5e7eb', background: '#fff', marginBottom: 16, color: '#111827' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 800, marginBottom: 4 }}>Pending membership approvals</div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>These requests came from invitation links and need organiser approval.</div>
                  </div>
                  <div style={{ fontWeight: 700 }}>{pending.length} request(s)</div>
                </div>

                {pendingMsg && (
                  <div style={{ marginTop: 10, fontSize: 14, color: pendingMsg.startsWith('✅') ? '#047857' : '#b91c1c' }}>
                    {pendingMsg}
                  </div>
                )}

                <div style={{ marginTop: 12, overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        <th style={{ ...thBase, textAlign: 'left' }}>Member</th>
                        <th style={{ ...thBase, textAlign: 'center' }}>Requested</th>
                        <th style={{ ...thBase, textAlign: 'center' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pending.map((p) => {
                        const label = `${p.given_name || ''} ${p.surname || ''}`.trim() || p.email || `User ${p.user_id}`;
                        return (
                          <tr key={p.user_id}>
                            <td style={tdLeft}>{label}</td>
                            <td style={tdCenter}>{p.count}</td>
                            <td style={tdCenter}>
                              <button
                                type="button"
                                className="action-button"
                                style={{ padding: '8px 10px', fontSize: 13, marginRight: 8, opacity: busy ? 0.6 : 1 }}
                                disabled={busy}
                                onClick={() => openApprove(p)}
                              >
                                Approve
                              </button>
                              <button
                                type="button"
                                className="action-button"
                                style={{ padding: '8px 10px', fontSize: 13, background: '#fff', color: '#b91c1c', border: '1px solid #fecaca', opacity: busy ? 0.6 : 1 }}
                                disabled={busy}
                                onClick={() => rejectPending(p.user_id)}
                              >
                                Reject
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {!loading && !err && approvedMembers.length === 0 && pending.length === 0 && (
              <div style={noteBox}>No members found.</div>
            )}


            {removeError ? (
              <div style={noteBox}>{removeError}</div>
            ) : null}

            {(biddingStarted === true && (Number(group?.owner_id) === Number(me?.id) || me?.access_level === 'superadmin')) ? (
              <div style={noteBox}>Bidding has started — members can no longer be removed.</div>
            ) : null}
            {!loading && !err && approvedMembers.length > 0 && (
              isMobile ? (
                // CARD LIST on mobile
                <div style={{ display: 'grid', gap: 12 }}>
                  {approvedMembers.map((m, idx) => {
                    const name = [m.given_name, m.surname].filter(Boolean).join(' ') || '—';
                                        const joined = fmtDDMMYYYY(m.joined_at || m.joined || m.created_at);
                    const memberships = Number(m.membershipCount || 0) || 0;
                    const membershipsDisplay = memberships > 0 ? memberships : '—';
	                    // membershipIndexes are used internally (e.g., remove a specific membership),
	                    // but we do not show "slots" in the UI.

                    return (
                      <div
                        key={m.id || idx}
                        style={{
                          background: '#fff',
                          border: '1px solid #e9eef5',
                          borderRadius: 10,
                          padding: 14,
                          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                        }}
                      >
                        <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>{name}</div>
                        {/* Contact details are hidden for privacy */}
<div style={kvRow}>
  <span style={kvKey}>Chat</span>
  <span style={kvVal}>
    {Number(m.user_id ?? m.userId ?? m.id) === Number(me?.id) ? (
      <span style={{ color: '#6b7280' }}>You</span>
    ) : (
      <Link
        to={`/messages/dm/u/${m.user_id ?? m.userId ?? m.id}`}
        style={{
          display: 'inline-block',
          background: '#0b3b6b',
          color: '#fff',
          padding: '6px 10px',
          borderRadius: 10,
          textDecoration: 'none',
          fontWeight: 700,
          fontSize: 13,
        }}
      >
        Message
      </Link>
    )}
  </span>
</div>
                        <div style={kvRow}>
                          <span style={kvKey}>Joined</span>
                          <span style={kvVal}>{joined}</span>
                        </div>
                        <div style={kvRow}>
                          <span style={kvKey}>Memberships</span>
                          <span style={kvVal}>
                            <strong>{membershipsDisplay}</strong>
                          </span>
                        </div>

                        {canRemoveMembers && canRemoveMemberRow(m) ? (
                          <div style={{ marginTop: 12 }}>
                            <button
                              type="button"
                              onClick={() => {
                                    const uid = m.user_id ?? m.userId ?? m.id;
                                    const label = `${m.given_name || ''} ${m.surname || ''}`.trim() || m.email || 'member';
                                    removeMember(uid, label);
                                  }}
                              disabled={removeBusyId === Number(m.user_id ?? m.userId ?? m.id)}
                              style={{
                                background: '#b91c1c',
                                color: '#fff',
                                border: 'none',
                                padding: '8px 12px',
                                borderRadius: 10,
                                cursor: removeBusyId === Number(m.user_id ?? m.userId ?? m.id) ? 'not-allowed' : 'pointer',
                                fontWeight: 700,
                                opacity: removeBusyId === Number(m.user_id ?? m.userId ?? m.id) ? 0.6 : 1,
                              }}
                            >
                              {removeBusyId === Number(m.user_id ?? m.userId ?? m.id) ? 'Removing...' : 'Remove'}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : (
                // TABLE on desktop
                <div style={{ overflowX: 'auto' }}>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'separate',
                      borderSpacing: 0,
                    }}
                  >
                    <thead>
                      <tr>
                        {(canRemoveMembers ? ['Member', 'Memberships', 'Joined', 'Chat', 'Actions'] : ['Member', 'Memberships', 'Joined', 'Chat']).map((h, i) => (
                          <th
                            key={h}
                            style={{
                              background: '#0b3b6b',
                              color: '#fff',
                              textAlign: i === 0 ? 'left' : 'center',
                              padding: '12px 14px',
                              fontWeight: 700,
                              fontSize: 14,
                              borderRight: '1px solid #0b3b6b',
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {approvedMembers.map((m, idx) => {
                        const name = [m.given_name, m.surname].filter(Boolean).join(' ') || '—';
                                                const joined = fmtDDMMYYYY(m.joined_at || m.joined || m.created_at);
                        const memberships = Number(m.membershipCount || 0) || 0;
                        const membershipsDisplay = memberships > 0 ? memberships : '—';
                        const stripe = idx % 2 === 0 ? '#ffffff' : '#f9fbfd';

                        return (
                          <tr key={m.id || idx} style={{ background: stripe }}>
                            <td style={tdLeft}>{name}</td>
                                                        <td style={tdCenter}>
                              <div style={{ fontWeight: 700 }}>{membershipsDisplay}</div>
                            </td>
                            <td style={tdCenter}>{joined}</td>
                            <td style={tdCenter}>
                              {Number(m.user_id ?? m.userId ?? m.id) === Number(me?.id) ? (
                                <span style={{ color: '#6b7280' }}>You</span>
                              ) : (
                                <Link
                                  to={`/messages/dm/u/${m.user_id ?? m.userId ?? m.id}`}
                                  style={{
                                    display: 'inline-block',
                                    background: '#0b3b6b',
                                    color: '#fff',
                                    padding: '6px 10px',
                                    borderRadius: 10,
                                    textDecoration: 'none',
                                    fontWeight: 700,
                                    fontSize: 13,
                                  }}
                                >
                                  Message
                                </Link>
                              )}
                            </td>
                      {canRemoveMembers ? (
                        <td style={tdCenter}>
                          {canRemoveMemberRow(m) ? (
                            <button
                              type="button"
                              onClick={() => {
                                    const uid = m.user_id ?? m.userId ?? m.id;
                                    const label = `${m.given_name || ''} ${m.surname || ''}`.trim() || m.email || 'member';
                                    removeMember(uid, label);
                                  }}
                              disabled={removeBusyId === Number(m.user_id ?? m.userId ?? m.id)}
                              style={{
                                background: '#b91c1c',
                                color: '#fff',
                                border: 'none',
                                padding: '6px 10px',
                                borderRadius: 10,
                                cursor: removeBusyId === Number(m.user_id ?? m.userId ?? m.id) ? 'not-allowed' : 'pointer',
                                fontWeight: 700,
                                opacity: removeBusyId === Number(m.user_id ?? m.userId ?? m.id) ? 0.6 : 1,
                                fontSize: 13,
                              }}
                            >
                              {removeBusyId === Number(m.user_id ?? m.userId ?? m.id) ? 'Removing...' : 'Remove'}
                            </button>
                          ) : (
                            <span style={{ color: '#94a3b8' }}>—</span>
                          )}
                        </td>
                      ) : null}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        </div>
      </div>

      {modalOpen && selectedPending && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 50,
          }}
          onClick={closeModal}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 520,
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 18px 50px rgba(0,0,0,0.18)',
              padding: 18,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 6 }}>Approve memberships</div>
            <div style={{ color: '#6b7280', fontSize: 13, marginBottom: 12 }}>
              Approve how many memberships for{' '}
              <strong>{`${selectedPending.given_name || ''} ${selectedPending.surname || ''}`.trim() || selectedPending.email || `User ${selectedPending.user_id}`}</strong>?
              {' '} (requested {selectedPending.count})
            </div>

            <input
              type="number"
              min={1}
              max={selectedPending.count}
              value={approveCount}
              onChange={(e) => setApproveCount(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb' }}
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
              <button
                type="button"
                className="action-button"
                style={{ background: '#fff', border: '1px solid #e5e7eb', color: '#111827', padding: '9px 12px', fontSize: 14 }}
                onClick={closeModal}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="action-button"
                style={{ padding: '9px 12px', fontSize: 14, opacity: busy ? 0.6 : 1 }}
                onClick={approveSelected}
                disabled={busy}
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- tiny style helpers ---------- */
const noteBox = {
  padding: 16,
  border: '1px solid #eef2f6',
  borderRadius: 8,
  color: '#6b7280',
};

const tdBase = {
  padding: '10px 14px',
  fontSize: 14,
  borderBottom: '1px solid #e9eef5',
};

// Header cell base style (used in the Pending approvals table)
const thBase = {
  padding: '10px 14px',
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: '0.02em',
  textTransform: 'uppercase',
  color: '#475569',
  borderBottom: '1px solid #e9eef5',
  whiteSpace: 'nowrap',
};
const tdLeft = { ...tdBase, textAlign: 'left', color: '#0f172a', fontWeight: 600 };
const tdCenter = { ...tdBase, textAlign: 'center', color: '#111827' };

const kvRow = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '6px 0',
  borderTop: '1px dashed #e9eef5',
};
const kvKey = { color: '#64748b', fontSize: 13, marginRight: 10 };
const kvVal = { color: '#0f172a', fontSize: 14, fontWeight: 600 };